import type { RouterContextType } from "@/api/permissions/permissions.api";
import {
  PLATFORM_LABELS,
  PLATFORM_ORDER,
  STATS_RANGE_DAYS,
} from "@/components/features/ShortLinks/shortLinks.constants";
import {
  buildClickSeries,
  buildDayAxis,
  buildInstallSeries,
} from "@/components/features/ShortLinks/buildChartSeries";
import { ShortLinkQr } from "@/components/features/ShortLinks/ShortLinkQr";
import { TimeSeriesChart } from "@/components/features/ShortLinks/TimeSeriesChart";
import styles from "@/components/features/ShortLinks/ShortLinks.module.scss";
import { Button } from "@/components/ui/Button/Button";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal/ConfirmationModal";
import {
  useDeleteShortLink,
  useShortLink,
  useShortLinkStats,
  useUpdateShortLink,
} from "@/hooks/deeplink/useShortLinks";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { Badge, Group, Stack, Switch, TextInput, Title } from "@mantine/core";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { requireSuperAdmin } from "@/utils/shortLinkAccess";

export const Route = createFileRoute("/_authenticated/short-links/$linkId")({
  component: ShortLinkDetailPage,
  beforeLoad: async (args) => {
    await requireSuperAdmin({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function ShortLinkDetailPage() {
  const { linkId } = useParams({ from: "/_authenticated/short-links/$linkId" });
  const navigate = useNavigate();

  const { data: link, isLoading } = useShortLink(linkId);

  // Computed once per mount rather than per render: a new Date() in the render
  // body would produce a new query key every time and refetch continuously.
  const range = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - STATS_RANGE_DAYS * 86_400_000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);

  const { data: stats } = useShortLinkStats(linkId, range);

  // The axis comes from the requested window, not from the returned rows: the
  // API omits days with no activity, so driving it off the rows would compress
  // quiet periods and draw a straight line across them as though nothing had
  // happened.
  const days = useMemo(() => buildDayAxis(range.from, range.to), [range]);
  const clickSeries = useMemo(() => buildClickSeries(stats, days), [stats, days]);
  const installSeries = useMemo(() => buildInstallSeries(stats, days), [stats, days]);
  const { mutateAsync: update, isPending: isSaving } = useUpdateShortLink();
  const { mutateAsync: remove, isPending: isDeleting } = useDeleteShortLink();

  const [targetUrl, setTargetUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  useBreadcrumb([{ title: "Short links", href: "/short-links" }, { title: link?.code ?? "Link" }]);

  if (isLoading || !link) return null;

  // The inputs fall back to the loaded record until the user edits them, so an
  // untouched field submits its current value rather than an empty string.
  const currentTarget = targetUrl ?? link.targetUrl;
  const currentExpiry = expiresAt ?? (link.expiresAt ? link.expiresAt.slice(0, 10) : "");

  const save = async () => {
    await update({
      id: linkId,
      payload: {
        targetUrl: currentTarget.trim(),
        // expiresAt is sent ONLY when the user touched the field, which is what
        // `expiresAt !== null` means here. Sending it unconditionally rewrote
        // the stored timestamp from a date-only input on every save: a link
        // expiring 2026-09-30T23:59:00Z came back at T00:00:00Z and died almost
        // a day early, silently, after an edit to an unrelated field. The input
        // is type="date" and cannot express the time, so it was unrecoverable.
        //
        // Explicit null still clears it: clearing the field sets state to "",
        // which is non-null and falsy, so it sends null deliberately.
        ...(expiresAt !== null
          ? {
              expiresAt: currentExpiry ? new Date(currentExpiry).toISOString() : null,
            }
          : {}),
      },
    });
  };

  const toggleActive = async () => {
    await update({ id: linkId, payload: { isActive: !link.isActive } });
  };

  // Named for what it does. It was called disableAndReturn while calling DELETE,
  // which is the sort of name that makes a destructive action read as reversible.
  const deleteAndReturn = async () => {
    await remove(linkId);
    setIsConfirmingDelete(false);
    navigate({ to: "/short-links" });
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>{link.title ?? link.code}</Title>
        <Badge color={link.isActive ? "green" : "gray"} variant="light">
          {link.isActive ? "Active" : "Disabled"}
        </Badge>
      </Group>

      <ShortLinkQr shortUrl={link.shortUrl} />

      <section>
        <Title order={4}>Last {STATS_RANGE_DAYS} days</Title>
        <div className={styles.statGrid}>
          {PLATFORM_ORDER.map((platform) => (
            <div key={platform} className={styles.statCard}>
              <div className={styles.statValue}>{stats?.totals[platform] ?? 0}</div>
              <div className={styles.statLabel}>{PLATFORM_LABELS[platform]}</div>
            </div>
          ))}
          <div className={styles.statCard}>
            <div className={styles.statValue}>{stats?.installs ?? 0}</div>
            <div className={styles.statLabel}>Installs</div>
          </div>
        </div>
        <TimeSeriesChart
          title="Clicks by platform"
          days={days}
          series={clickSeries}
          summary={`Daily clicks for ${link.code} over the last ${STATS_RANGE_DAYS} days, split by iOS, Android and desktop or other.`}
        />

        {/* A SECOND CHART, not a second y-axis. Installs are the same unit as
            clicks but a completely different scale, and a dual-axis chart is
            the single most misleading thing a time series can do: the crossover
            point is an artefact of the two scales, not of the data. */}
        <TimeSeriesChart
          title="Installs"
          days={days}
          series={installSeries}
          summary={`Daily installs attributed to ${link.code} over the last ${STATS_RANGE_DAYS} days.`}
        />

        <p className={styles.mutedCell}>
          Crawler hits are recorded but excluded from the click totals above. Installs and clicks
          are not expected to reconcile, and are charted separately for that reason: a click does
          not imply an install, and on iOS an install arrives with no matching click at all.
        </p>
      </section>

      <section>
        <Title order={4}>Settings</Title>
        <Stack gap="md">
          <TextInput
            label="Destination"
            value={currentTarget}
            onChange={(event) => setTargetUrl(event.currentTarget.value)}
          />
          <TextInput
            type="date"
            label="Expires"
            description="Clear this to remove the expiry entirely."
            value={currentExpiry}
            onChange={(event) => setExpiresAt(event.currentTarget.value)}
          />
          <Switch
            label="Link is active"
            checked={link.isActive}
            onChange={toggleActive}
            description="A disabled link sends visitors to the fallback instead of its destination."
          />
          <Group>
            <Button onClick={save} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
            <Button variant="destructive-secondary" onClick={() => setIsConfirmingDelete(true)}>
              Delete link
            </Button>
          </Group>
        </Stack>
      </section>

      <ConfirmationModal
        isOpen={isConfirmingDelete}
        onClose={() => setIsConfirmingDelete(false)}
        title="Delete this short link?"
        body={
          <>
            A link already in circulation cannot be un-published. Anyone who has {link.code} saved,
            printed or sent on will get the fallback instead of its destination, and this cannot be
            undone.
            <br />
            <br />
            To stop the link working while keeping it, turn off &quot;Link is active&quot; instead.
          </>
        }
        confirmText="Delete link"
        confirmVariant="destructive-primary"
        isConfirmLoading={isDeleting}
        onConfirm={deleteAndReturn}
      />
    </Stack>
  );
}
